import { useState } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { OnboardingProject, StageSignOff, SubmitSignOffRequest } from '@/types/onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  CheckCircle2,
  XCircle,
  Clock,
  User,
  FileText,
  AlertCircle,
  ArrowRight,
  Mail,
  Send,
  History,
  CalendarDays,
  UserCheck,
  Building2,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { 
  useSignOffs, 
  useSubmitSignOff, 
  useInviteCustomer, 
  useSignOffHistory,
  useCustomerInvitations,
  SignOffActivity 
} from '../hooks/useSignOff';

interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

const STAGES = [
  { name: 'data_import', label: 'Data Import', description: 'Data has been imported and validated' },
  { name: 'validation', label: 'Validation', description: 'Data quality checks have passed' },
  { name: 'mapping', label: 'Mapping', description: 'Bill sources and GL codes are configured' },
  { name: 'configuration', label: 'Configuration', description: 'All settings are complete' },
  { name: 'review', label: 'Review', description: 'System tests have passed' },
  { name: 'customer_signoff', label: 'Customer Sign-Off', description: 'Customer approval received' },
];

const Stage6SignOff = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, loadProject } = useOutletContext<OutletContext>();

  // Hooks for data fetching
  const { data: signOffs = [], isLoading } = useSignOffs(projectId);
  const { data: history = [] } = useSignOffHistory(projectId);
  const { data: invitations = [] } = useCustomerInvitations(projectId);
  
  // Mutations
  const submitSignOff = useSubmitSignOff();
  const inviteCustomer = useInviteCustomer();

  // Local state
  const [comments, setComments] = useState<Record<string, string>>({});
  const [conditions, setConditions] = useState<Record<string, Record<string, boolean>>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    customer_email: project?.customer_contact_email || '',
    customer_name: project?.customer_contact_name || '',
    personal_message: '',
    expires_days: 7,
  });

  // Get sign-off for a stage (from DB or generate default)
  const getSignOffForStage = (stageName: string): StageSignOff => {
    const dbSignOff = signOffs.find(s => s.stage_name === stageName);
    if (dbSignOff) return dbSignOff;

    // Return default pending sign-off
    return {
      id: `temp-${stageName}`,
      project_id: projectId || '',
      stage_name: stageName,
      status: 'pending',
      signed_by_user_id: null,
      signed_at: null,
      comments: null,
      conditions_met: {},
      required_signoff: true,
      signoff_role: stageName === 'customer_signoff' ? 'customer' : 'internal',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  };

  const handleSignOff = async (stageName: string, status: 'approved' | 'rejected') => {
    if (!project || !projectId) return;

    try {
      setSubmitting(stageName);
      
      const request: SubmitSignOffRequest = {
        stage_name: stageName,
        status,
        comments: comments[stageName] || undefined,
        conditions_met: conditions[stageName] || {},
      };

      await submitSignOff.mutateAsync({ projectId, request });
      
      toast({
        title: 'Sign-Off Submitted',
        description: `Stage ${stageName} has been ${status === 'approved' ? 'approved' : 'rejected'}`,
      });

      await loadProject();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit sign-off',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(null);
    }
  };

  const handleInviteCustomer = async () => {
    if (!projectId) return;

    try {
      await inviteCustomer.mutateAsync({
        projectId,
        request: inviteForm,
      });
      setInviteDialogOpen(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-destructive" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-600 hover:bg-green-700">Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };

  const allStages = STAGES.map(stage => ({
    ...stage,
    signOff: getSignOffForStage(stage.name),
  }));

  const allSignOffsComplete = allStages.every(s => s.signOff.status === 'approved');
  const approvedCount = allStages.filter(s => s.signOff.status === 'approved').length;
  const pendingInvitation = invitations.find(inv => inv.status === 'pending' || inv.status === 'sent');

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-24 w-full" />
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Skeleton key={i} className="h-40 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Stage 6: Customer Sign-Off</h2>
        <p className="text-muted-foreground mt-1">
          Review and approve each stage before go-live
        </p>
      </div>

      {/* Overall Status */}
      <Card className={allSignOffsComplete ? 'border-green-200 bg-green-50 dark:bg-green-950/20' : 'border-amber-200 bg-amber-50 dark:bg-amber-950/20'}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {allSignOffsComplete ? (
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              ) : (
                <AlertCircle className="h-8 w-8 text-amber-600" />
              )}
              <div>
                <h3 className="text-xl font-semibold">
                  {allSignOffsComplete ? 'All Stages Approved' : 'Sign-Off In Progress'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {approvedCount} of {allStages.length} stages approved
                </p>
              </div>
            </div>
            {allSignOffsComplete && (
              <Button
                onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-7`)}
              >
                Proceed to Go-Live
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Customer Invitation Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Customer Invitation
              </CardTitle>
              <CardDescription>
                Invite the customer to review and approve the onboarding
              </CardDescription>
            </div>
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" disabled={!!pendingInvitation}>
                  <Send className="h-4 w-4 mr-2" />
                  {pendingInvitation ? 'Invitation Pending' : 'Invite Customer'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Customer for Sign-Off</DialogTitle>
                  <DialogDescription>
                    Send an email invitation to the customer to review and sign off on this project.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Customer Email</Label>
                    <Input
                      type="email"
                      value={inviteForm.customer_email}
                      onChange={e => setInviteForm({ ...inviteForm, customer_email: e.target.value })}
                      placeholder="customer@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Customer Name</Label>
                    <Input
                      value={inviteForm.customer_name}
                      onChange={e => setInviteForm({ ...inviteForm, customer_name: e.target.value })}
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Personal Message (Optional)</Label>
                    <Textarea
                      value={inviteForm.personal_message}
                      onChange={e => setInviteForm({ ...inviteForm, personal_message: e.target.value })}
                      placeholder="Add a personal message for the customer..."
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expires In (Days)</Label>
                    <Input
                      type="number"
                      value={inviteForm.expires_days}
                      onChange={e => setInviteForm({ ...inviteForm, expires_days: parseInt(e.target.value) || 7 })}
                      min={1}
                      max={30}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleInviteCustomer}
                    disabled={!inviteForm.customer_email || inviteCustomer.isPending}
                  >
                    {inviteCustomer.isPending ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        {(invitations.length > 0 || pendingInvitation) && (
          <CardContent>
            <div className="space-y-3">
              {invitations.map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{inv.customer_email}</p>
                      <p className="text-xs text-muted-foreground">
                        {inv.customer_name} • Sent {inv.sent_at ? new Date(inv.sent_at).toLocaleDateString() : 'Pending'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={inv.status === 'completed' ? 'default' : 'secondary'}>
                    {inv.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Sign-Off Stages */}
      <div className="space-y-4">
        {allStages.map((stage, index) => {
          const { signOff } = stage;
          const isCustomerStage = stage.name === 'customer_signoff';
          const canSignOff = signOff.status === 'pending' || signOff.status === 'rejected';

          return (
            <Card
              key={stage.name}
              className={
                signOff.status === 'approved'
                  ? 'border-green-200'
                  : signOff.status === 'rejected'
                  ? 'border-destructive/50'
                  : ''
              }
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(signOff.status)}
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        Stage {index + 1}: {stage.label}
                        {isCustomerStage && (
                          <Badge variant="outline" className="ml-2">
                            <Building2 className="h-3 w-3 mr-1" />
                            Customer Required
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription>{stage.description}</CardDescription>
                    </div>
                  </div>
                  {getStatusBadge(signOff.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Signed info */}
                {signOff.signed_at && signOff.signed_by_user_id && (
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      <span>
                        {signOff.signoff_role === 'customer' ? 'Customer' : 'Internal Team'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarDays className="h-4 w-4" />
                      <span>{new Date(signOff.signed_at).toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {/* Comments */}
                {signOff.comments && (
                  <div className="p-3 bg-muted rounded-md">
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium mb-1">Comments</p>
                        <p className="text-sm text-muted-foreground">{signOff.comments}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sign-off form */}
                {canSignOff && (
                  <div className="space-y-4 pt-4 border-t">
                    <div>
                      <Label>Comments (Optional)</Label>
                      <Textarea
                        value={comments[stage.name] || ''}
                        onChange={(e) =>
                          setComments({ ...comments, [stage.name]: e.target.value })
                        }
                        placeholder="Add any comments or notes about this stage..."
                        rows={3}
                      />
                    </div>

                    {isCustomerStage && (
                      <div className="space-y-2">
                        <Label>I confirm that:</Label>
                        <div className="space-y-2">
                          {[
                            { key: 'data_accurate', label: 'All data is accurate and complete' },
                            { key: 'configurations_correct', label: 'All configurations are correct' },
                            { key: 'ready_for_go_live', label: 'I am ready to proceed with go-live' },
                          ].map(condition => (
                            <div key={condition.key} className="flex items-center space-x-2">
                              <Checkbox
                                id={`condition-${condition.key}-${stage.name}`}
                                checked={conditions[stage.name]?.[condition.key] || false}
                                onCheckedChange={(checked) =>
                                  setConditions({
                                    ...conditions,
                                    [stage.name]: {
                                      ...conditions[stage.name],
                                      [condition.key]: checked as boolean,
                                    },
                                  })
                                }
                              />
                              <label
                                htmlFor={`condition-${condition.key}-${stage.name}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                              >
                                {condition.label}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSignOff(stage.name, 'approved')}
                        disabled={submitting === stage.name}
                        className="flex-1"
                      >
                        {submitting === stage.name ? 'Submitting...' : 'Approve'}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleSignOff(stage.name, 'rejected')}
                        disabled={submitting === stage.name}
                        className="flex-1"
                      >
                        {submitting === stage.name ? 'Submitting...' : 'Reject'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Sign-Off History */}
      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Sign-Off History
            </CardTitle>
            <CardDescription>Timeline of sign-off activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {history.slice(0, 10).map((activity, idx) => (
                <div key={activity.id} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${
                      activity.action === 'approved' ? 'bg-green-500' :
                      activity.action === 'rejected' ? 'bg-destructive' :
                      'bg-muted-foreground'
                    }`} />
                    {idx < history.slice(0, 10).length - 1 && (
                      <div className="w-px h-full bg-border" />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="text-sm font-medium">
                      {activity.stage_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - {' '}
                      <span className={
                        activity.action === 'approved' ? 'text-green-600' :
                        activity.action === 'rejected' ? 'text-destructive' :
                        'text-muted-foreground'
                      }>
                        {activity.action.charAt(0).toUpperCase() + activity.action.slice(1)}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                    {activity.details?.comments && (
                      <p className="text-sm text-muted-foreground mt-1">
                        "{activity.details.comments}"
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <Separator />
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-5`)}
        >
          ← Previous Stage
        </Button>
        <Button
          onClick={() => {
            if (project && allSignOffsComplete) {
              navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-7`);
            } else {
              toast({
                title: 'Sign-Off Required',
                description: 'All stages must be approved before proceeding to go-live',
                variant: 'destructive',
              });
            }
          }}
          disabled={!allSignOffsComplete}
        >
          Continue to Go-Live →
        </Button>
      </div>
    </div>
  );
};

export default Stage6SignOff;
